import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { EditableComponent } from '../../../../shared/components/interfaces/editable.component';
import { RequestUrlUtils } from '../../../../shared/utils/request.url.utils';
import { AssetTypeModel } from '../../../asset/components/model/asset-type.model';
import { AssetService } from '../../../asset/service/asset.service';
import { MakeModel } from '../../../make/model/make.model';
import { AuthService } from '../../../security/service/auth.service';
import { ProductUIMessages } from '../../model/product-ui.messages';
import { ProductModel } from '../../model/product.model';
import { ProductWarrantyDuration } from '../../model/product_warranty_duration';
import { ProductRoutingStates } from '../../product-routing.module';
import { ProductService } from '../../service/product.service';
import { ProductBaseComponent } from '../product-base.component';

@Component({
  selector: 'product-edit',
  templateUrl:
    './console/app-js/modules/product/components/edit/product-edit.component.html'
})
export class ProductEditComponent extends ProductBaseComponent
  implements OnInit, EditableComponent {
  private originalProduct = new ProductModel();
  private productId: number;
  private loadingProduct = true;
  protected nameAlreadyInUse = false;
  private saving = false;

  /**
   * @constructor
   * @param {ToastrService} toastr
   * @param {NgProgressService} progressService
   * @param product
   * @param {ProductService} productService
   */
  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    productService: ProductService,
    assetService: AssetService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    super(progressService, toastr, authService, productService, assetService);
    this.productId = this.activatedRoute.snapshot.params['id'];
    this.partAndProductModel.id = this.productId;
    this.loadproductWarantyDuration();
  }

  /**
   * Initiates the Module
   */
  ngOnInit(): void {
    this.loadProduct();
    this.loadMakesList();
    this.loadAssetTypes();
  }

  /**
   * Populate the Entire Product
   */
  protected loadProduct(): void {
    this.loadingProduct = true;
    this.progressRef.start();
    this.productService.getAllDataProductById(this.productId).subscribe(
      (res: any) => {
        this.originalProduct = res;
        this.partAndProductModel = { ...this.originalProduct };
        this.partAndProductModel.laborInformation = {
          ...this.originalProduct.laborInformation
        };
        this.partAndProductModel.isActive = this.productService.isProductActive(
          this.partAndProductModel.endDate
        );
        this.partAndProductModel.make = new MakeModel();
        this.partAndProductModel.make.id = this.partAndProductModel.makeId;
        this.partAndProductModel.assetType = new AssetTypeModel();
        this.partAndProductModel.assetType.id = this.partAndProductModel.assetTypeId;
        this.loadingProduct = false;
        this.progressRef.complete();
      },
      () => this.onAPIError(ProductUIMessages.LOAD_PRODUCT_ERROR)
    );
  }

  /**
   * Compare if current object has changed comparing previous version
   * @returns {boolean}
   */
  isDirty(): boolean {
    return (
      JSON.stringify(this.originalProduct) !==
      JSON.stringify(this.partAndProductModel)
    );
  }

  /**
   * Returns user to View state for the product.
   */
  onCancel(): void {
    this.router.navigate([
      `/console/${ProductRoutingStates.LIST.url}`,
      this.originalProduct.id,
      'view'
    ]);
  }

  /**
   * 1. Validates if product name is not already in use.
   * 2. Saves product information.
   */
  onSave(): void {
    this.saving = true;
    this.productService.saveProduct(this.partAndProductModel).subscribe(
      (result: any) => {
        this.saving = false;
        let productId = result.results.id;
        this.onAPISuccess(
          ProductUIMessages.EDIT_PRODUCT_SUCCESS.replace('{0}', productId)
        );
        this.router.navigate([
          `/console/${ProductRoutingStates.LIST.url}`,
          productId,
          'view'
        ]);
      },
      () => {
        this.saving = false;
        this.onAPIError(
          ProductUIMessages.EDIT_PRODUCT_ERROR.replace('{0}', '')
        );
      }
    );
  }

  /**
   * Saving process for product of type 'Part'
   * @deprecated Not used anymore
   */
  private savePartProduct(): void {
    // 1. validate if name is in use
    this.productService
      .getProductByProductName(this.partAndProductModel.name)
      .subscribe(
        (validateNameResponse: any) => {
          this.nameAlreadyInUse =
            (validateNameResponse.results.length > 0 ||
              validateNameResponse.results.length > 0) &&
            this.originalProduct.name !== this.partAndProductModel.name;
          if (!this.nameAlreadyInUse) {
            this.progressRef.start();
            // 2. save model name, receive id generated.
            this.productService.saveProduct(this.partAndProductModel).subscribe(
              (saveProductResponse: any) => {
                let productId = RequestUrlUtils.getLastID(
                  saveProductResponse.results.links[0].href
                );
                // 3. save labor information, redirect to product view if success.
                this.productService
                  .saveLaborInformation(this.partAndProductModel, productId)
                  .subscribe(
                    (saveLaborInfoResponse: any) => {
                      if (saveLaborInfoResponse.responseCode === 200) {
                        this.saving = false;
                        this.onAPISuccess(
                          ProductUIMessages.EDIT_PRODUCT_SUCCESS.replace(
                            '{0}',
                            this.partAndProductModel.name
                          )
                        );
                        // this.stateService.go(PartAndProductStates.VIEW.name, {
                        //   id: productId
                        // });
                      } else {
                        this.saving = false;
                        this.onAPIError(
                          ProductUIMessages.EDIT_PRODUCT_LABOUR_INFO_ERROR.replace(
                            '{0}',
                            this.partAndProductModel.name
                          )
                        );
                      }
                    },
                    () =>
                      this.onAPIError(
                        ProductUIMessages.EDIT_PRODUCT_LABOUR_INFO_ERROR.replace(
                          '{0}',
                          this.partAndProductModel.name
                        )
                      )
                  );
              },
              () =>
                this.onAPIError(
                  ProductUIMessages.EDIT_PRODUCT_ERROR.replace(
                    '{0}',
                    this.partAndProductModel.name
                  )
                )
            );
          } else {
            this.saving = false;
          }
        },
        () =>
          this.onAPIError(
            ProductUIMessages.VALIDATION_PRODUCT_NAME_ERROR.replace(
              '{0}',
              this.partAndProductModel.name
            )
          )
      );
  }

  protected loadproductWarantyDuration(): ProductWarrantyDuration {
    this.productService
      .getProductDurationDaysById(this.partAndProductModel.id)
      .subscribe((res: any) => {
        if (res) {
          this.productWarrantyDuration.laborWarrantyDays = res.laborWarrantyDays;
          this.productWarrantyDuration.partsWarrantyDays = res.partsWarrantyDays;
        } else {
          this.productWarrantyDuration.laborWarrantyDays = 0;
          this.productWarrantyDuration.partsWarrantyDays = 0;
        }
      });
    return this.productWarrantyDuration;
  }

  protected onSaveWarantyDetails() {
    this.saving = true;
    let laborAndPartsWarrantyDuration = new ProductWarrantyDuration();
    laborAndPartsWarrantyDuration.productId = this.partAndProductModel.id;
    laborAndPartsWarrantyDuration.laborWarrantyDays = this.productWarrantyDuration
      .laborWarrantyDays
      ? this.productWarrantyDuration.laborWarrantyDays
      : 0;
    laborAndPartsWarrantyDuration.partsWarrantyDays = this.productWarrantyDuration
      .partsWarrantyDays
      ? this.productWarrantyDuration.partsWarrantyDays
      : 0;
    this.productService
      .editeWarrantyDuration(laborAndPartsWarrantyDuration)
      .subscribe(
        (res: any) => {
          this.saving = false;
          this.onAPISuccess(
            ProductUIMessages.EDIT_EWARRANTYDURATION_SUCCESS.replace(
              '{0}',
              laborAndPartsWarrantyDuration.productId.toString()
            )
          );
          this.router.navigate([
            `/console/${ProductRoutingStates.LIST.url}`,
            laborAndPartsWarrantyDuration.productId,
            'view'
          ]);
        },
        () => {
          this.saving = false;
          this.onAPIError(
            ProductUIMessages.EDIT_EWARRANTYDURATION_ERROR.replace('{0}', '')
          );
        }
      );
  }
}
